import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Trash, Check, X } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

export default function Timesheets() {
  const { role } = useAuth();
  const [items, setItems] = useState(null);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [ts, setTs] = useState(null);
  const canValidate = ["super_admin", "direction_generale", "responsable", "direction_commerciale"].includes(role);

  const load = () => api.get("/timesheets").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const openNew = () => { setTs({ semaine: new Date().toISOString().slice(0, 10), statut: "Brouillon", lignes: [{ jour: "Lundi", projet: "", tache: "", debut: "", fin: "", duree: 0 }] }); setOpen(true); };
  const setLine = (i, k, v) => setTs((p) => { const l = [...p.lignes]; l[i] = { ...l[i], [k]: k === "duree" ? Number(v) : v }; return { ...p, lignes: l }; });
  const addLine = () => setTs((p) => ({ ...p, lignes: [...p.lignes, { jour: "Lundi", projet: "", tache: "", debut: "", fin: "", duree: 0 }] }));
  const rmLine = (i) => setTs((p) => ({ ...p, lignes: p.lignes.filter((_, j) => j !== i) }));

  const save = async (statut) => {
    const payload = { ...ts, statut };
    try {
      if (ts.id) await api.put(`/timesheets/${ts.id}`, payload); else await api.post("/timesheets", payload);
      toast.success(statut === "Soumis" ? "Feuille soumise" : "Feuille enregistrée"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const decide = async (id, statut) => { try { await api.patch(`/timesheets/${id}/decision`, { statut }); toast.success(`Feuille ${statut.toLowerCase()}e`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const totalH = (t) => (t.lignes || []).reduce((s, l) => s + (l.duree || 0), 0);

  return (
    <div>
      <PageHeader title="Feuilles de temps" subtitle="Feuilles hebdomadaires · soumission et validation">
        <Button onClick={openNew} data-testid="add-timesheet" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle feuille</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="Aucune feuille de temps" subtitle="Créez votre feuille hebdomadaire par projet et tâche." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="rounded-xl border bg-white p-5">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-slate-400 font-mono">Semaine du {t.semaine}</p><p className="font-semibold">{t.user_name}</p></div>
                <StatusBadge value={t.statut} />
              </div>
              <div className="mt-3 space-y-1">
                {(t.lignes || []).map((l, i) => (
                  <div key={i} className="flex justify-between text-xs text-slate-600 border-b py-1"><span>{l.jour} · {l.projet} — {l.tache}</span><span className="font-mono">{l.duree}h</span></div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3">
                <p className="text-sm font-semibold">Total : <span className="font-mono">{totalH(t)}h</span></p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setTs(t); setOpen(true); }} data-testid={`edit-timesheet-${t.id}`}>Ouvrir</Button>
                  {canValidate && t.statut === "Soumis" && (
                    <>
                      <Button size="sm" onClick={() => decide(t.id, "Validé")} className="bg-emerald-600 text-white" data-testid={`validate-${t.id}`}><Check className="h-4 w-4" /></Button>
                      <Button size="sm" onClick={() => decide(t.id, "Rejeté")} className="bg-rose-600 text-white" data-testid={`reject-${t.id}`}><X className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Feuille de temps hebdomadaire</DialogTitle></DialogHeader>
          {ts && (
            <div className="space-y-4">
              <div className="w-48"><Label className="text-xs">Semaine (date de début)</Label><Input type="date" value={ts.semaine} onChange={(e) => setTs({ ...ts, semaine: e.target.value })} className="mt-1" /></div>
              <div className="rounded-lg border overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="p-2">Jour</th><th className="p-2">Projet</th><th className="p-2">Tâche</th><th className="p-2">Début</th><th className="p-2">Fin</th><th className="p-2">Durée(h)</th><th></th></tr></thead>
                  <tbody>
                    {ts.lignes.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1"><Select value={l.jour} onValueChange={(v) => setLine(i, "jour", v)}><SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger><SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></td>
                        <td className="p-1"><Input value={l.projet} onChange={(e) => setLine(i, "projet", e.target.value)} className="h-8 w-32" /></td>
                        <td className="p-1"><Input value={l.tache} onChange={(e) => setLine(i, "tache", e.target.value)} className="h-8 w-32" /></td>
                        <td className="p-1"><Input type="time" value={l.debut} onChange={(e) => setLine(i, "debut", e.target.value)} className="h-8 w-24" /></td>
                        <td className="p-1"><Input type="time" value={l.fin} onChange={(e) => setLine(i, "fin", e.target.value)} className="h-8 w-24" /></td>
                        <td className="p-1"><Input type="number" value={l.duree} onChange={(e) => setLine(i, "duree", e.target.value)} className="h-8 w-16" /></td>
                        <td><Button type="button" variant="ghost" size="icon" onClick={() => rmLine(i)}><Trash className="h-4 w-4 text-rose-500" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" />Ajouter une ligne</Button>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => save("Brouillon")}>Enregistrer brouillon</Button>
                <Button type="button" onClick={() => save("Soumis")} className="bg-brand-600 text-white" data-testid="submit-timesheet">Soumettre</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
